import { ACCRUALS_CONFIG_TABS } from './accrualsConfigurationConstants';
import {
  ESTIMATION_ALL_VENDORS_ID,
  ESTIMATION_METHOD_DEFINITIONS,
  ESTIMATION_METHODS_BY_APPLY_ON,
  MOCK_ESTIMATION_RULES,
  createEmptyEstimationRule,
} from './accrualsEstimationRulesConstants';
import { createEmptyRuleMeta, normalizeRuleMeta } from './accrualsRulesBuilderUtils';
import {
  groupsToConditionTree,
  resolveEstimationGroups,
} from './accrualsEstimationGroupsUtils';

function isLegacyConditionRow(item) {
  return item && typeof item === 'object' && 'field' in item && !('name' in item) && !('estimationMethod' in item);
}

function migrateLegacyConditions(conditions = []) {
  if (!Array.isArray(conditions) || !conditions.length) return [];
  if (!conditions.every(isLegacyConditionRow)) return conditions;
  return [
    createEmptyEstimationRule({
      id: 'rule-migrated',
      name: 'Migrated rule',
      conditions,
      ruleMeta: createEmptyRuleMeta(conditions.length),
    }),
  ];
}

export function normalizeEstimationRules(rules = []) {
  if (!Array.isArray(rules)) return [];
  const migrated = migrateLegacyConditions(rules);
  return migrated.map((rule) => normalizeEstimationRule(rule));
}

export function normalizeEstimationRule(rule = {}) {
  const applyOn = rule.applyOn === 'po' ? 'po' : 'vendor';
  const methodIds = ESTIMATION_METHODS_BY_APPLY_ON[applyOn];
  const estimationMethod = methodIds.includes(rule.estimationMethod)
    ? rule.estimationMethod
    : methodIds[0];

  const empty = createEmptyEstimationRule({ applyOn, estimationMethod });
  const grouped = resolveEstimationGroups(rule, empty.conditions);
  return {
    ...empty,
    ...rule,
    applyOn,
    estimationMethod,
    groups: grouped.groups,
    groupLogic: grouped.groupLogic,
    groupConnectors: grouped.groupConnectors,
    conditions: grouped.conditions,
    ruleMeta: normalizeRuleMeta(
      { ...(rule.ruleMeta || {}), connectors: grouped.connectors },
      grouped.conditions.length,
    ),
    methodConfig: {
      ...empty.methodConfig,
      ...(rule.methodConfig || {}),
    },
    vendorIds: Array.isArray(rule.vendorIds) ? rule.vendorIds : [],
    effectiveFrom: toDateInput(rule.effectiveFrom || rule.effective_from) || '2026-01-01',
    effectiveTo: toDateInput(rule.effectiveTo || rule.effective_to) || '2026-12-31',
  };
}

export function loadEstimationRulesFromStorage(rawRules) {
  if (!Array.isArray(rawRules) || !rawRules.length) {
    return MOCK_ESTIMATION_RULES.map((rule) => normalizeEstimationRule({ ...rule }));
  }
  return normalizeEstimationRules(rawRules);
}

export function countEstimationRules(rules = []) {
  return Array.isArray(rules) ? rules.length : 0;
}

export function serializeEstimationRulesForStorage(rules = []) {
  return rules.map((rule) => normalizeEstimationRule(rule));
}

export function getEstimationTabStorageKey() {
  return ACCRUALS_CONFIG_TABS.ESTIMATION;
}

export function estimationRuleId(rule = {}) {
  return String(rule.id || rule._id || '');
}

export function isEstimationDraftRule(rule = {}) {
  const id = estimationRuleId(rule);
  return !id || id.startsWith('rule-') || id.startsWith('__draft__');
}

export function uniqueEstimationRules(rules = []) {
  const byId = {};
  const order = [];
  (rules || []).forEach((rule) => {
    if (!rule) return;
    const id = estimationRuleId(rule) || `__anon__${order.length}`;
    if (!Object.prototype.hasOwnProperty.call(byId, id)) order.push(id);
    byId[id] = rule;
  });
  return order.map((id) => byId[id]);
}

const METHOD_ID_BY_CODE = {
  'AP-01': 'ap-01',
  'AP-02': 'ap-02',
  'AP-03': 'ap-03',
  'PO-01': 'po-01',
  'PO-02': 'po-02',
  'PO-03': 'po-03',
};

function toDateInput(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    return `${us[3]}-${String(us[1]).padStart(2, '0')}-${String(us[2]).padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return toDateInput(parsed);
}

function toIsoDate(value) {
  const day = toDateInput(value);
  if (!day) return null;
  return `${day}T00:00:00.000Z`;
}

export function apiRuleToUi(rule = {}) {
  const source = rule.source === 'open_po' ? 'po' : 'vendor';
  const code = String(rule.template_code || '').toUpperCase();
  const estimationMethod = METHOD_ID_BY_CODE[code] || (source === 'po' ? 'po-01' : 'ap-01');
  const vendorScope = rule.vendor_scope || {};
  const vendorIds = vendorScope.mode === 'supplier_ids'
    ? (vendorScope.supplier_ids || []).filter(Boolean)
    : [ESTIMATION_ALL_VENDORS_ID];
  return normalizeEstimationRule({
    id: rule._id || rule.id,
    _id: rule._id || null,
    name: rule.name || 'Untitled rule',
    badge: code || '',
    active: rule.status !== 'disabled',
    applyOn: source,
    estimationMethod,
    methodConfig: rule.template_settings || {},
    effectiveFrom: toDateInput(rule.effective_from || rule.effectiveFrom),
    effectiveTo: toDateInput(rule.effective_to || rule.effectiveTo),
    vendorIds,
    conditions: (rule.conditions || []).map((c, index) => ({
      id: c.id || `condition-${c.field || 'x'}-${index}`,
      field: c.field || '',
      operator: c.operator || c.op || 'eq',
      value: c.value === null || c.value === undefined ? '' : String(c.value),
    })),
    condition_tree: rule.condition_tree || null,
    groups: Array.isArray(rule.groups) ? rule.groups : [],
    groupLogic: rule.group_logic || rule.groupLogic || '',
    groupConnectors: rule.group_connectors || rule.groupConnectors || [],
    ruleMeta: {
      connectors: Array.isArray(rule.connectors) ? rule.connectors : [],
      useCustomPattern: false,
      customPattern: '',
    },
    defaultGlEnabled: Boolean(rule.default_gl?.enabled),
    debitGl: rule.default_gl?.debit_gl || '',
    creditGl: rule.default_gl?.credit_gl || '',
    execution_sequence: rule.execution_sequence,
  });
}

export function uiRuleToApi(rule = {}, { executionSequence } = {}) {
  const applyOn = rule.applyOn === 'po' ? 'po' : 'vendor';
  const source = applyOn === 'po' ? 'open_po' : 'ap_postings';
  const definition = ESTIMATION_METHOD_DEFINITIONS[rule.estimationMethod] || {};
  const templateCode = definition.code
    || (applyOn === 'po' ? 'PO-01' : 'AP-01');
  const vendorIds = (rule.vendorIds || []).filter((id) => id && id !== ESTIMATION_ALL_VENDORS_ID);
  const allVendors = applyOn === 'po'
    || (rule.vendorIds || []).includes(ESTIMATION_ALL_VENDORS_ID);
  const payload = {
    name: rule.name,
    status: rule.active === false ? 'disabled' : 'saved',
    source,
    template_code: templateCode,
    template_settings: {},
    effective_from: toIsoDate(rule.effectiveFrom),
    effective_to: toIsoDate(rule.effectiveTo),
    vendor_scope: allVendors
      ? { mode: 'all_vendors', supplier_ids: [] }
      : { mode: 'supplier_ids', supplier_ids: vendorIds },
    conditions: (rule.conditions || []).map((c) => ({
      field: c.field,
      operator: c.operator || c.op,
      value: c.value,
    })),
    connectors: (rule.ruleMeta && rule.ruleMeta.connectors) || [],
    condition_tree: groupsToConditionTree(rule.groups, rule.groupConnectors),
    default_gl: {
      enabled: Boolean(rule.defaultGlEnabled),
      debit_gl: rule.debitGl || null,
      credit_gl: rule.creditGl || null,
    },
  };
  if (rule._id && !String(rule._id).startsWith('rule-') && !String(rule._id).startsWith('__draft__')) {
    payload._id = rule._id;
  } else if (rule.id && !String(rule.id).startsWith('rule-') && !String(rule.id).startsWith('__draft__')) {
    payload._id = rule.id;
  }
  if (Number(executionSequence) >= 1) {
    payload.execution_sequence = executionSequence;
  } else if (Number(rule.execution_sequence) >= 1) {
    payload.execution_sequence = rule.execution_sequence;
  }
  return payload;
}

